import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiCreditCard, FiFileText, FiCheckCircle, FiClock, FiDollarSign, FiDownload, FiAlertCircle } from 'react-icons/fi';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

function BillingView({ userId }) {
  const [billings, setBillings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchBillings();
  }, [userId, filter]);

  const fetchBillings = async () => {
    try {
      setLoading(true);
      const url = filter === 'all'
        ? `${API_BASE}/billing/?user_id=${userId}`
        : `${API_BASE}/billing/?user_id=${userId}&status=${filter}`;

      const response = await axios.get(url);
      if (response.data.success) {
        setBillings(response.data.data);
        setSummary(response.data.summary);
        setError(null);
      }
    } catch (err) {
      setError('Failed to load billing records');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = (billing) => {
    const invoiceContent = `
Invoice: ${billing.invoice_number}
Date: ${new Date(billing.created_at).toLocaleDateString('en-IN')}

Lawyer: ${billing.lawyer_name}
Service: ${billing.service_type}
Consultation Date: ${new Date(billing.consultation_date).toLocaleDateString('en-IN')}

Consultation Fee: ₹${billing.consultation_fee.toFixed(2)}
Platform Fee: ₹${billing.platform_fee.toFixed(2)}
GST (18%): ₹${billing.gst.toFixed(2)}
────────────────────────
Total: ₹${billing.total_amount.toFixed(2)}

Payment Status: ${billing.payment_status}
Payment Method: ${billing.payment_method || 'N/A'}
Transaction ID: ${billing.transaction_id || 'N/A'}
    `;

    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(invoiceContent));
    element.setAttribute('download', `${billing.invoice_number}.txt`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const statusColors = {
    pending: 'bg-amber-50 text-amber-700',
    completed: 'bg-green-50 text-green-700',
    paid: 'bg-green-50 text-green-700',
    failed: 'bg-red-50 text-red-600',
    refunded: 'bg-purple-50 text-purple-700',
  };

  if (loading && billings.length === 0) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      <span className="ml-3 text-gray-500">Loading billing records...</span>
    </div>
  );
  if (error) return <div className="text-center py-12 text-red-500">{error}</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FiCreditCard className="w-5 h-5 text-accent" />
          Billing & Invoices
        </h2>
        <p className="text-sm text-gray-500 mt-1">View invoices and payment history</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Total Invoices', value: summary.total_invoices, icon: FiFileText, color: 'text-primary', bg: 'bg-primary/5' },
            { label: 'Paid Invoices', value: summary.paid_invoices, icon: FiCheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Pending Invoices', value: summary.pending_invoices, icon: FiClock, color: 'text-amber-600', bg: 'bg-amber-50' },
            { label: 'Total Paid', value: `₹${summary.total_paid.toFixed(2)}`, icon: FiDollarSign, color: 'text-green-600', bg: 'bg-green-50' },
            { label: 'Total Pending', value: `₹${summary.total_pending.toFixed(2)}`, icon: FiAlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-card p-4">
              <div className={`w-9 h-9 rounded-xl ${card.bg} flex items-center justify-center mb-2`}>
                <card.icon className={`w-4 h-4 ${card.color}`} />
              </div>
              <p className="text-lg font-bold text-gray-900">{card.value}</p>
              <p className="text-xs text-gray-500">{card.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', 'pending', 'completed', 'failed', 'refunded'].map(status => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              filter === status
                ? 'bg-primary text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {billings.length === 0 ? (
          <div className="p-12 text-center">
            <FiCreditCard className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500">No {filter !== 'all' ? filter : ''} invoices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
                  <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Lawyer</th>
                  <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Consultation Date</th>
                  <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {billings.map(billing => (
                  <tr key={billing.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-gray-900 text-sm">{billing.invoice_number}</p>
                      <p className="text-xs text-gray-400">{new Date(billing.created_at).toLocaleDateString('en-IN')}</p>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700">{billing.lawyer_name}</td>
                    <td className="px-4 py-4 text-sm text-gray-600">{new Date(billing.consultation_date).toLocaleDateString('en-IN')}</td>
                    <td className="px-4 py-4">
                      <div className="text-xs text-gray-500 space-y-0.5">
                        <p>Service: ₹{billing.consultation_fee.toFixed(2)}</p>
                        <p>Platform: ₹{billing.platform_fee.toFixed(2)}</p>
                        <p>GST: ₹{billing.gst.toFixed(2)}</p>
                        <p className="font-bold text-sm text-gray-900 pt-1">₹{billing.total_amount.toFixed(2)}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${statusColors[billing.payment_status] || 'bg-gray-100 text-gray-600'}`}>
                        {billing.payment_status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => downloadInvoice(billing)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary/5 text-primary hover:bg-primary/10 rounded-xl text-xs font-medium transition-colors"
                      >
                        <FiDownload className="w-3.5 h-3.5" /> Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default BillingView;
